/**
 * Unified ContentService — core types.
 *
 * A `ContentRef` is a SOURCE-AGNOSTIC logical reference to a piece of content.
 * It carries no hint about where the bytes live (S3/CloudFront, server disk, dev
 * disk, backend proxy). The `ContentResolver` maps it to a concrete `ContentHandle`
 * (a URL + metadata) by running a per-namespace ordered fallback chain.
 *
 * This is the single contract that every namespace (gallery, hospitality,
 * curriculum, fsi, radio, photoAlbum, studio, kb) and the mobile app
 * (`site-android2`) share, replacing the ~6 hand-rolled per-feature resolution
 * paths documented in `docs/content-service-design.md`.
 */

/**
 * @typedef { 'gallery' | 'hospitality' | 'curriculum' | 'fsi'
 *          | 'radio' | 'photoAlbum' | 'studio' | 'kb' } ContentNamespace
 */

/**
 * @typedef { 'image' | 'video' | 'audio' | 'json' | 'pdf' | 'hls' } ContentType
 */

/**
 * @typedef { 'thumb' | 'reduced' | 'full' | 'avif' | 'webp' } ContentVariant
 */

/**
 * @typedef { 'disk' | 'static' | 'cf-signed' | 's3-presigned' | 'backend-proxy' } ContentSource
 */

/**
 * @typedef {Object} ContentRef
 * @property {ContentNamespace} namespace
 * @property {string} logicalKey            Source-agnostic key, e.g.
 *                                          'gallery/hiking/2024/x/photo.webp' or
 *                                          'hospitality-menu/regional/x/images/dish.webp'.
 *                                          Should NOT include a leading slash or a host
 *                                          — EXCEPT for the `radio` namespace, whose
 *                                          logicalKey is a full external stream URL that
 *                                          must keep its scheme://host (the backend
 *                                          proxy re-fetches it).
 * @property {ContentType} contentType
 * @property {ContentVariant} [variant]     'thumb' | 'reduced' | 'full' | 'avif' | 'webp'
 * @property {boolean} [requireSigned]      Default true in prod for media.
 * @property {string} [assetHint]          Optional per-namespace hint (e.g. hospitality
 *                                          dataRoot/rel) used by adapters that need more
 *                                          than a flat logicalKey.
 */

/**
 * @typedef {Object} ContentHandle
 * @property {string} url                   The concrete URL the client should fetch.
 * @property {ContentSource} source         Which adapter produced it.
 * @property {Record<string,string>} [headers]
 * @property {boolean} [isStream]           true for /api/.../stream + radio HLS (302 redirect).
 * @property {number} [expiresAt]           ms epoch; from CloudFront Expires or presign expiry.
 * @property {number} ttl                    Cache lifetime (ms).
 */

/** Strip a leading slash (and any host) so a logicalKey is always path-relative. */
export function normalizeLogicalKey(key, opts = {}) {
  if (!key) return '';
  let k = String(key).trim();
  // drop scheme://host[:port] unless the caller needs to preserve it
  // (radio logicalKeys are external stream URLs that must keep their host).
  if (!opts.preserveHost) {
    const slash = k.indexOf('://');
    if (slash !== -1) {
      k = k.slice(slash + 3);
      const p = k.indexOf('/');
      k = p === -1 ? '' : k.slice(p);
    }
  }
  // collapse backslashes (Windows paths from dev disk)
  k = k.replace(/\\/g, '/');
  // remove leading slash(es)
  k = k.replace(/^\/+/, '');
  return k;
}

/** True when a value looks like an absolute http(s) URL. */
export function isAbsoluteUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value);
}
